import { useState, useEffect, useCallback } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend,
} from "recharts";
import {
  fetchPipelineQueue, fetchPipelineSync, fetchPipelineImport, fetchPipelineRefs, fetchPipelineTagging,
  fetchPipelineScoring, fetchPipelineCpe, fetchPipelinePrevalence,
} from "../api";
import { fmtTs, fmtTime } from "../utils/time";

// An overview, then one tab per pipeline stage, in pipeline order.
const TABS = ["queue", "sync", "import", "refs", "tagging", "scoring", "cpe", "prevalence"];
const TAB_LABELS = {
  queue: "Queue", sync: "Repo Sync", import: "CVE Import", refs: "Reference Collection",
  tagging: "Reference Tagging", scoring: "Scoring", cpe: "CPE Selection", prevalence: "Prevalence",
};
const TAB_FETCHERS = {
  queue: fetchPipelineQueue, sync: fetchPipelineSync, import: fetchPipelineImport, refs: fetchPipelineRefs,
  tagging: fetchPipelineTagging, scoring: fetchPipelineScoring, cpe: fetchPipelineCpe,
  prevalence: fetchPipelinePrevalence,
};
const REFRESH_MS = 10000;

function StatTile({ label, value, total, color, title }) {
  return (
    <div className="panel" title={title}
         style={{ padding: "12px 18px", minWidth: 140, textAlign: "center" }}>
      <div style={{ fontSize: 26, fontWeight: 700, color: color || "var(--text-primary)" }}>
        {value}
      </div>
      <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
        {label}{total != null && <span style={{ color: "var(--text-muted)" }}> / {total}</span>}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    running: { bg: "rgba(88,166,255,0.15)", fg: "#58a6ff" },
    idle: { bg: "rgba(63,185,80,0.15)", fg: "#3fb950" },
    waiting: { bg: "rgba(63,185,80,0.15)", fg: "#3fb950" },
    stopping: { bg: "rgba(210,153,34,0.15)", fg: "#d29922" },
    down: { bg: "rgba(248,81,73,0.15)", fg: "#f85149" },
  };
  const s = styles[status] || styles.down;
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 3,
      textTransform: "uppercase", letterSpacing: "0.3px", background: s.bg, color: s.fg,
    }}>
      {status}
    </span>
  );
}

function fmtDuration(secs) {
  if (secs == null) return "-";
  const s = Math.max(0, Math.round(secs));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

// Ages older than this mean the import loop (900s interval) has stalled.
const STALE_SECS = 2 * 3600;

function ageColor(iso, now) {
  if (!iso) return "var(--text-muted)";
  return (now - new Date(iso).getTime()) / 1000 > STALE_SECS ? "#f85149" : "var(--text-primary)";
}

// Last 24 hours as hourly buckets; hours (or keys) with no rows are zero.
function hourlySeries(rows, now, keys = ["count"]) {
  const byHour = Object.fromEntries(rows.map((r) => [new Date(r.hour).getTime(), r]));
  const top = new Date(now);
  top.setMinutes(0, 0, 0);
  const out = [];
  for (let i = 23; i >= 0; i--) {
    const t = top.getTime() - i * 3600 * 1000;
    const bucket = { hour: `${String(new Date(t).getHours()).padStart(2, "0")}:00` };
    for (const k of keys) bucket[k] = byHour[t]?.[k] || 0;
    out.push(bucket);
  }
  return out;
}

function ServiceUnreachable({ error }) {
  return (
    <div className="error" style={{ marginTop: 8 }}>
      Service unreachable: {error}. Database counts below are still current.
    </div>
  );
}

function Banner({ children }) {
  return <div className="error" style={{ marginTop: 8 }}>{children}</div>;
}

// Header for the stages added after the first three: name, status, next pass,
// progress bar while running, and the unreachable / last-error notices.
function ServiceHeader({ name, what, svc, error, now, progress, right, children }) {
  const status = svc ? svc.status : "down";
  const running = svc && svc.status === "running";
  const nextRunIn = svc && !running && svc.next_run ? svc.next_run - now / 1000 : null;
  const pct = running && svc.total ? Math.round((svc.done / svc.total) * 100) : null;
  return (
    <div style={{ padding: "4px 0" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <strong>{name}</strong>
        {what && <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>{what}</span>}
        <StatusBadge status={status} />
        {nextRunIn != null && nextRunIn > 0 && (
          <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>
            next pass in {fmtDuration(nextRunIn)}
          </span>
        )}
        {right && (
          <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--text-secondary)" }}>{right}</span>
        )}
      </div>
      {error && <ServiceUnreachable error={error} />}
      {svc && svc.error && <Banner>Last pass error: {svc.error}</Banner>}
      {running && pct != null && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 4 }}>
            Pass started {fmtTs(svc.started_at)} — {svc.done} / {svc.total} ({pct}%){progress ? `, ${progress}` : ""}
          </div>
          <div style={{ height: 8, background: "var(--border)", borderRadius: 4, overflow: "hidden" }}>
            <div style={{ width: `${pct}%`, height: "100%", background: "#58a6ff" }} />
          </div>
        </div>
      )}
      {children}
    </div>
  );
}

const STATUS_COLORS = { ok: "#3fb950", tagged: "#3fb950", scored: "#3fb950", imported: "#3fb950",
  fetched: "#3fb950", failed: "#f85149", error: "#f85149" };

// columns: [{ label, key, render?, align? }]
function RecentTable({ title, rows, columns, empty }) {
  return (
    <div className="panel" style={{ marginBottom: 12 }}>
      <div className="panel-header">{title} ({rows.length})</div>
      {rows.length === 0 ? (
        <div style={{ padding: 12, fontSize: 13, color: "var(--text-secondary)" }}>{empty}</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid var(--border)" }}>
                {columns.map((c) => (
                  <th key={c.label} style={{ textAlign: c.align || "left", padding: 8 }}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.cve}-${i}`} style={{ borderBottom: "1px solid var(--border)" }}>
                  {columns.map((c) => (
                    <td key={c.label} style={{
                      padding: 8, textAlign: c.align || "left", color: "var(--text-secondary)",
                      maxWidth: 520, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>
                      {c.render ? c.render(r) : r[c.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const TIME_COL = { label: "Time (UTC)", key: "ts" };
const CVE_COL = {
  label: "CVE",
  render: (r) => (
    <a href={`/cve/${r.cve}`} style={{ color: "var(--accent)", fontWeight: 600 }}>{r.cve}</a>
  ),
};
const STATUS_COL = {
  label: "Status",
  render: (r) => <span style={{ color: STATUS_COLORS[r.status] || "#d29922" }}>{r.status}</span>,
};

function HourlyChart({ title, rows, now, bars }) {
  return (
    <div className="panel" style={{ marginBottom: 12 }}>
      <div className="panel-header">{title}</div>
      <div style={{ height: 240, padding: "8px 8px 0" }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={hourlySeries(rows, now, bars.map((b) => b.key))}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
            <XAxis dataKey="hour" tick={{ fontSize: 11 }} interval={2} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={40} />
            <Tooltip />
            {bars.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
            {bars.map((b) => (
              <Bar key={b.key} dataKey={b.key} name={b.name} stackId="s" fill={b.color} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function Tiles({ children }) {
  return <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>{children}</div>;
}

function SectionLabel({ children }) {
  return (
    <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-secondary)", textTransform: "uppercase",
                  letterSpacing: "0.4px", margin: "4px 0 8px" }}>
      {children}
    </div>
  );
}

function ImportTab({ data, now }) {
  const svc = data.service;
  const db = data.db;
  const status = svc ? svc.status : "down";
  const running = svc && svc.status === "running";
  const pct = running && svc.total ? Math.round((svc.done / svc.total) * 100) : null;
  const nextRunIn = svc && svc.next_run ? svc.next_run - now / 1000 : null;

  return (
    <>
      <div className="panel" style={{ marginBottom: 12, padding: "12px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <strong>vinnie-cve-import</strong>
          <StatusBadge status={status} />
          {svc && !running && nextRunIn != null && (
            <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>
              next pass in {fmtDuration(nextRunIn)}
            </span>
          )}
          {svc && !running && nextRunIn == null && (
            svc.last_import ? (
              <span style={{ fontSize: 12, color: "#d29922" }}>
                loop stopped — no pass scheduled
              </span>
            ) : (
              <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                waiting for first pass
              </span>
            )
          )}
          {svc && (
            <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--text-secondary)" }}
                  title="deltaLog fetchTime of the newest imported entry">
              Watermark: {fmtTs(svc.last_fetch_time)}
            </span>
          )}
        </div>
        {data.service_error && <ServiceUnreachable error={data.service_error} />}
        {svc && svc.error && (
          <div className="error" style={{ marginTop: 8 }}>Last pass error: {svc.error}</div>
        )}
        {running && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 4 }}>
              Pass started {fmtTs(svc.started_at)} — {svc.done} / {svc.total} ({pct}%)
            </div>
            <div style={{ height: 8, background: "var(--border)", borderRadius: 4, overflow: "hidden" }}>
              <div style={{ width: `${pct}%`, height: "100%", background: "#58a6ff" }} />
            </div>
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <StatTile label="New CVEs (24h)" value={db.new_24h} />
        <StatTile label="CVEs updated (24h)" value={db.updated_24h}
                  title="Any write to the CVE row, including later pipeline stages" />
        <StatTile label="Last new CVE" value={fmtTs(db.latest_imported_at)}
                  color={ageColor(db.latest_imported_at, now)} />
        {svc && (
          <>
            <StatTile label={running ? "Imported (this pass)" : "Imported (last pass)"}
                      value={svc.imported} total={svc.total} />
            <StatTile label="Missed" value={svc.missed} total={svc.total}
                      color={svc.missed > 0 ? "#d29922" : "#3fb950"} />
            <StatTile label={running ? "Elapsed" : "Last pass took"} value={fmtDuration(svc.elapsed)} />
            <StatTile label="Last pass finished" value={fmtTs(svc.last_import)}
                      color={ageColor(svc.last_import, now)} />
          </>
        )}
      </div>

      <div className="panel" style={{ marginBottom: 12 }}>
        <div className="panel-header">New CVEs per hour (last 24h)</div>
        <div style={{ height: 220, padding: "8px 8px 0" }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={hourlySeries(db.hourly, now)}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis dataKey="hour" tick={{ fontSize: 11 }} interval={2} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={36} />
              <Tooltip />
              <Bar dataKey="count" name="New CVEs" fill="#58a6ff" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {svc && (
        <div className="panel">
          <div className="panel-header">Recent activity ({svc.recent.length})</div>
          {svc.recent.length === 0 ? (
            <div style={{ padding: 12, fontSize: 13, color: "var(--text-secondary)" }}>
              Nothing imported since the service started.
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid var(--border)" }}>
                    <th style={{ textAlign: "left", padding: 8 }}>Time (UTC)</th>
                    <th style={{ textAlign: "left", padding: 8 }}>CVE</th>
                    <th style={{ textAlign: "left", padding: 8 }}>Status</th>
                    <th style={{ textAlign: "left", padding: 8 }}>Scores found</th>
                  </tr>
                </thead>
                <tbody>
                  {svc.recent.map((r, i) => (
                    <tr key={`${r.cve}-${i}`} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: 8, color: "var(--text-secondary)" }}>{r.ts}</td>
                      <td style={{ padding: 8 }}>
                        <a href={`/cve/${r.cve}`} style={{ color: "var(--accent)", fontWeight: 600 }}>
                          {r.cve}
                        </a>
                      </td>
                      <td style={{ padding: 8, color: r.status === "imported" ? "#3fb950" : "#d29922" }}>
                        {r.status}
                      </td>
                      <td style={{ padding: 8, color: "var(--text-secondary)" }}>{r.scores}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </>
  );
}

// No fetch for this long while CVEs wait on refs means the collector is stalled
// (it runs every 15 min, and a pass normally logs progress every few seconds).
const REF_STALL_SECS = 3600;

function fmtAge(iso, now) {
  return iso ? `${fmtDuration((now - new Date(iso).getTime()) / 1000)} ago` : "never";
}

function RefsTab({ data, now }) {
  const svc = data.service;
  const db = data.db;
  const status = svc ? svc.status : "down";
  const running = svc && svc.status === "running";
  const pct = running && svc.total ? Math.round((svc.done / svc.total) * 100) : null;
  const nextRunIn = svc && svc.status === "waiting" && svc.next_run ? svc.next_run - now / 1000 : null;
  const sinceFetch = db.latest_fetched_at ? (now - new Date(db.latest_fetched_at).getTime()) / 1000 : null;
  const stalled = db.cves_waiting > 0 && (sinceFetch == null || sinceFetch > REF_STALL_SECS);
  const h = db.last_24h;
  const attempted = (h.fetched || 0) + (h.failed || 0);
  const successPct = attempted ? Math.round(((h.fetched || 0) / attempted) * 100) : null;

  return (
    <>
      <div className="panel" style={{ marginBottom: 12, padding: "12px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <strong>vinnie-ref-collector</strong>
          <StatusBadge status={status} />
          {nextRunIn != null && (
            <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>
              next pass in {fmtDuration(nextRunIn)}
            </span>
          )}
          {svc && svc.run_number != null && (
            <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--text-secondary)" }}>
              Pass #{svc.run_number} since restart
            </span>
          )}
        </div>
        {stalled && (
          <div className="error" style={{ marginTop: 8 }}>
            No references fetched for {fmtAge(db.latest_fetched_at, now)} while {db.cves_waiting} CVEs
            are waiting. The collector looks stalled; scoring waits on it.
          </div>
        )}
        {data.service_error && <ServiceUnreachable error={data.service_error} />}
        {svc && svc.error && (
          <div className="error" style={{ marginTop: 8 }}>Last pass error: {svc.error}</div>
        )}
        {running && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 4 }}>
              Pass started {fmtTs(svc.started_at)} — {svc.done} / {svc.total} refs ({pct}%),{" "}
              {svc.rate}/min
            </div>
            <div style={{ height: 8, background: "var(--border)", borderRadius: 4, overflow: "hidden" }}>
              <div style={{ width: `${pct}%`, height: "100%", background: "#58a6ff" }} />
            </div>
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <StatTile label={`CVEs waiting on refs (${db.window_days}d)`} value={db.cves_waiting}
                  total={db.cves_in_window} color={stalled ? "#f85149" : undefined}
                  title="CVEs in the collector's window with a ref not yet in the cache; scoring skips them until it is. Updated every 60s." />
        <StatTile label="Oldest waiting CVE imported" value={fmtTs(db.oldest_waiting_imported_at)} />
        <StatTile label="Last ref fetched" value={fmtTs(db.latest_fetched_at)}
                  color={stalled ? "#f85149" : "var(--text-primary)"} />
        <StatTile label="Fetched (24h)" value={h.fetched || 0} color="#3fb950" />
        <StatTile label="Failed (24h)" value={h.failed || 0}
                  color={(h.failed || 0) > 0 ? "#f85149" : undefined} />
        <StatTile label="Skipped (24h)" value={h.skipped || 0}
                  title="Skip-listed domain/URL, or a binary file" />
        <StatTile label="Success rate (24h)" value={successPct == null ? "-" : `${successPct}%`}
                  title="Fetched / (fetched + failed); skipped refs excluded" />
      </div>

      <div className="panel" style={{ marginBottom: 12 }}>
        <div className="panel-header">References processed per hour (last 24h)</div>
        <div style={{ height: 240, padding: "8px 8px 0" }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={hourlySeries(db.hourly, now, ["fetched", "failed", "skipped"])}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis dataKey="hour" tick={{ fontSize: 11 }} interval={2} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={40} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="fetched" name="Fetched" stackId="s" fill="#3fb950" />
              <Bar dataKey="failed" name="Failed" stackId="s" fill="#f85149" />
              <Bar dataKey="skipped" name="Skipped" stackId="s" fill="#8b949e" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {svc && (
        <div className="panel">
          <div className="panel-header">Recent fetches ({svc.recent.length})</div>
          {svc.recent.length === 0 ? (
            <div style={{ padding: 12, fontSize: 13, color: "var(--text-secondary)" }}>
              Nothing fetched since the service started.
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid var(--border)" }}>
                    <th style={{ textAlign: "left", padding: 8 }}>Time (UTC)</th>
                    <th style={{ textAlign: "left", padding: 8 }}>CVE</th>
                    <th style={{ textAlign: "left", padding: 8 }}>Status</th>
                    <th style={{ textAlign: "right", padding: 8 }}>Size</th>
                    <th style={{ textAlign: "left", padding: 8 }}>URL</th>
                  </tr>
                </thead>
                <tbody>
                  {svc.recent.map((r, i) => (
                    <tr key={`${r.hash}-${i}`} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: 8, color: "var(--text-secondary)" }}>{r.ts}</td>
                      <td style={{ padding: 8 }}>
                        <a href={`/cve/${r.cve}`} style={{ color: "var(--accent)", fontWeight: 600 }}>
                          {r.cve}
                        </a>
                      </td>
                      <td style={{
                        padding: 8,
                        color: r.status === "fetched" ? "#3fb950" : r.status === "failed" ? "#f85149" : "var(--text-secondary)",
                      }}>
                        {r.status}
                      </td>
                      <td style={{ padding: 8, textAlign: "right", color: "var(--text-secondary)" }}>
                        {r.size ? `${(r.size / 1000).toFixed(1)} KB` : "-"}
                      </td>
                      <td style={{ padding: 8, maxWidth: 480, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        <a href={r.url} target="_blank" rel="noopener noreferrer" title={r.url}
                           style={{ color: "var(--text-secondary)" }}>
                          {r.url}
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </>
  );
}

function TaggingTab({ data, now }) {
  const svc = data.service;
  const db = data.db;
  const st = (k) => db.states[k] || { refs: 0, cves: 0 };
  const status = svc ? svc.status : "down";
  const running = svc && svc.status === "running";
  const pct = running && svc.total ? Math.round((svc.done / svc.total) * 100) : null;
  const nextRunIn = svc && !running && svc.next_run ? svc.next_run - now / 1000 : null;
  // tag_one_cve() reports "skipped" when no batch got any reply at all, which
  // means the LLM call itself failed (e.g. OpenRouter HTTP 402, out of credits).
  const allFailed = svc && !running && svc.total > 0 && svc.tagged === 0 && svc.skipped === svc.total;
  const maxTag = Math.max(1, ...db.top_tags.map((t) => t.count));

  return (
    <>
      <div className="panel" style={{ marginBottom: 12, padding: "12px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <strong>vinnie-ref-tagging</strong>
          <StatusBadge status={status} />
          {nextRunIn != null && (
            <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>
              next pass in {fmtDuration(nextRunIn)}
            </span>
          )}
          {svc && (
            <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--text-secondary)" }}>
              {svc.provider} · {svc.model} · {svc.target_workers} workers
            </span>
          )}
        </div>
        {allFailed && (
          <div className="error" style={{ marginTop: 8 }}>
            The last pass got no reply from the model for any of its {svc.total} CVEs (0 tagged), so
            the LLM API itself is failing — for example OpenRouter out of credits (HTTP 402). Check
            the vinnie-ref-tagging log. Nothing gets scored until its refs are tagged.
          </div>
        )}
        {data.service_error && <ServiceUnreachable error={data.service_error} />}
        {svc && svc.error && (
          <div className="error" style={{ marginTop: 8 }}>Last pass error: {svc.error}</div>
        )}
        {running && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 4 }}>
              Pass started {fmtTs(svc.started_at)} — {svc.done} / {svc.total} CVEs ({pct}%),{" "}
              {svc.worker_count} workers active
            </div>
            <div style={{ height: 8, background: "var(--border)", borderRadius: 4, overflow: "hidden" }}>
              <div style={{ width: `${pct}%`, height: "100%", background: "#58a6ff" }} />
            </div>
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <StatTile label={`CVEs waiting on tags (${db.window_days}d)`} value={db.cves_waiting}
                  total={db.cves_with_refs} color={allFailed ? "#f85149" : undefined}
                  title="CVEs with at least one untagged ref; scoring skips them. Updated every 60s." />
        <StatTile label="Refs queued" value={st("queued").refs}
                  title="Sent to the model on the next pass" />
        <StatTile label={`Refs held (${db.hold_hours}h)`} value={st("held").refs}
                  color={st("held").refs > 0 ? "#d29922" : undefined}
                  title="The model's reply could not be parsed; retried after the hold" />
        <StatTile label={`Refs in ${db.retry_days}d back-off`} value={st("backoff").refs}
                  color={st("backoff").refs > 0 ? "#d29922" : undefined}
                  title={`The model answered with no tags; retried after ${db.retry_days} days. Blocks ${st("backoff").cves} CVEs from scoring until then.`} />
        <StatTile label="Tagged by LLM" value={st("llm").refs} color="#3fb950" />
        <StatTile label="Auto-tagged" value={st("auto").refs}
                  title="Tagged by domain rule (e.g. git.kernel.org) with no LLM call" />
        {svc && (
          <StatTile label={running ? "Tagged (this pass)" : "Tagged (last pass)"} value={svc.tagged}
                    total={svc.total} color={allFailed ? "#f85149" : undefined} />
        )}
      </div>

      <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <div className="panel" style={{ flex: "2 1 420px", minWidth: 0 }}>
          <div className="panel-header">Refs tagged per hour (last 24h)</div>
          <div style={{ height: 240, padding: "8px 8px 0" }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={hourlySeries(db.hourly, now, ["llm", "auto", "backoff"])}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="hour" tick={{ fontSize: 11 }} interval={2} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={40} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="llm" name="Tagged by LLM" stackId="s" fill="#3fb950" />
                <Bar dataKey="auto" name="Auto-tagged" stackId="s" fill="#58a6ff" />
                <Bar dataKey="backoff" name="Answered, no tags" stackId="s" fill="#d29922" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="panel" style={{ flex: "1 1 260px", minWidth: 0 }}>
          <div className="panel-header">Most common tags ({db.window_days}d)</div>
          <div style={{ padding: "8px 12px" }}>
            {db.top_tags.map((t) => (
              <div key={t.tag} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, margin: "4px 0" }}>
                <span style={{ width: 110, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      title={t.tag}>
                  {t.tag}
                </span>
                <div style={{ flex: 1, background: "var(--border)", borderRadius: 3, height: 10 }}>
                  <div style={{ width: `${(t.count / maxTag) * 100}%`, height: "100%", background: "#58a6ff", borderRadius: 3 }} />
                </div>
                <span style={{ width: 52, textAlign: "right", color: "var(--text-secondary)" }}>{t.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {svc && (
        <div className="panel">
          <div className="panel-header">Recent CVEs ({svc.recent.length})</div>
          {svc.recent.length === 0 ? (
            <div style={{ padding: 12, fontSize: 13, color: "var(--text-secondary)" }}>
              Nothing tagged since the service started.
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid var(--border)" }}>
                    <th style={{ textAlign: "left", padding: 8 }}>Time (UTC)</th>
                    <th style={{ textAlign: "left", padding: 8 }}>CVE</th>
                    <th style={{ textAlign: "left", padding: 8 }}>Status</th>
                    <th style={{ textAlign: "left", padding: 8 }}>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {svc.recent.map((r, i) => (
                    <tr key={`${r.cve}-${i}`} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: 8, color: "var(--text-secondary)" }}>{r.ts}</td>
                      <td style={{ padding: 8 }}>
                        <a href={`/cve/${r.cve}`} style={{ color: "var(--accent)", fontWeight: 600 }}>
                          {r.cve}
                        </a>
                      </td>
                      <td style={{
                        padding: 8,
                        color: r.status === "tagged" ? "#3fb950" : r.status === "failed" ? "#f85149" : "#d29922",
                      }}>
                        {r.status}
                      </td>
                      <td style={{ padding: 8, color: "var(--text-secondary)", maxWidth: 520, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          title={r.scores}>
                        {r.scores}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </>
  );
}

// repos-refresh.sh runs every 15 minutes; an hour without a sync is a stall.
const SYNC_STALE_SECS = 3600;

function SyncTab({ data, now }) {
  const svc = data.service;
  const age = svc && svc.last_sync ? (now - new Date(svc.last_sync).getTime()) / 1000 : null;
  const stale = svc && svc.status !== "running" && (age == null || age > SYNC_STALE_SECS);
  return (
    <>
      <div className="panel" style={{ marginBottom: 12, padding: "12px 16px" }}>
        <ServiceHeader name="vinnie-repo-sync" what="git pull of cvelistV5 and vulnrichment"
                       svc={svc} error={data.service_error} now={now} />
        {stale && (
          <Banner>
            No successful sync for {fmtAge(svc.last_sync, now)}. CVE import reads these clones, so new
            CVEs stop arriving until it syncs again.
          </Banner>
        )}
      </div>
      {svc && (
        <>
          <Tiles>
            <StatTile label="Last sync" value={fmtTs(svc.last_sync)}
                      color={stale ? "#f85149" : "var(--text-primary)"} />
            <StatTile label={svc.status === "running" ? "Elapsed" : "Last sync took"}
                      value={fmtDuration(svc.elapsed)} />
          </Tiles>
          <div className="panel">
            <div className="panel-header">Output of the last sync</div>
            <pre style={{ margin: 0, padding: 12, fontSize: 12, whiteSpace: "pre-wrap",
                          color: "var(--text-secondary)", maxHeight: 360, overflow: "auto" }}>
              {(svc.output || []).join("\n") || "(no output yet)"}
            </pre>
          </div>
        </>
      )}
    </>
  );
}

// Scoring runs hourly; a day without a new score while CVEs are ready is a stall.
const SCORE_STALL_SECS = 24 * 3600;

function lastPass(svc) {
  if (!svc || !svc.total) return null;
  return `last pass: ${svc.scored} scored, ${svc.skipped} skipped, ${svc.failed} failed of ${svc.total}`;
}

function ScoringTab({ data, now }) {
  const comp = data.services.components;
  const cwe = data.services.cwe;
  const db = data.db;
  const f = db.funnel;
  const since = (iso) => (iso ? (now - new Date(iso).getTime()) / 1000 : null);
  const compAge = since(db.last_components_at);
  const stalled = f.comp_queued > 0 && (compAge == null || compAge > SCORE_STALL_SECS);
  const compositeAge = since(db.last_composite_at);
  const allFailed = (s) => s && s.status !== "running" && s.total > 0 && s.failed > 0 && s.scored === 0;

  return (
    <>
      <div className="panel" style={{ marginBottom: 12, padding: "12px 16px" }}>
        <ServiceHeader name="vinnie-llm-component-scoring" what="CVSS 3.1 · CVSS 4.0 · SSVC"
                       svc={comp.state} error={comp.error} now={now} right={lastPass(comp.state)}
                       progress={comp.state && `${comp.state.worker_count} workers`} />
        <div style={{ borderTop: "1px solid var(--border)", margin: "10px 0" }} />
        <ServiceHeader name="vinnie-llm-scoring" what="CWE"
                       svc={cwe.state} error={cwe.error} now={now} right={lastPass(cwe.state)}
                       progress={cwe.state && `${cwe.state.worker_count} workers`} />
        {stalled && (
          <Banner>
            No CVE has been scored since {fmtTs(db.last_components_at)} ({fmtAge(db.last_components_at, now)})
            while {f.comp_queued} CVEs are ready. Scoring runs hourly once a CVE's refs are fetched and tagged.
          </Banner>
        )}
        {(allFailed(comp.state) || allFailed(cwe.state)) && (
          <Banner>
            Every CVE in the last scoring pass failed with none scored, so the LLM API itself is failing
            (for example OpenRouter out of credits, HTTP 402). Each failure blocks that CVE for {db.retry_days} days.
          </Banner>
        )}
        {f.comp_blocked > 0 && (
          <Banner>
            {f.comp_blocked} CVEs that are ready to score are blocked for up to {db.retry_days} days by
            earlier failed attempts (LLM_ATTEMPT markers). <code>scripts/cleanup-llm-attempts.py --retry-days 0</code>{" "}
            removes the markers so the next pass retries them.
          </Banner>
        )}
        {f.cwe_scored > 0 && (compositeAge == null || compositeAge > 7 * 86400) && (
          <Banner>
            {db.last_composite_at
              ? `No composite CWE has been formed since ${fmtTs(db.last_composite_at)}.`
              : "No CVE in the window has a composite CWE."}{" "}
            The composite needs at least 2 CWE voters, but only {db.model} scores CWE now.
          </Banner>
        )}
      </div>

      <SectionLabel>Funnel ({db.window_days}d window)</SectionLabel>
      <Tiles>
        <StatTile label="CVEs in window" value={f.in_window} />
        <StatTile label="Waiting on refs" value={f.waiting_refs}
                  title="Refs not all fetched and tagged yet; scoring skips these" />
        <StatTile label="Ready to score" value={f.ready} />
        {f.no_refs > 0 && (
          <StatTile label="No refs (never scored)" value={f.no_refs} color="#d29922"
                    title="The scoring query requires at least one ref" />
        )}
      </Tiles>
      <SectionLabel>CVSS 3.1 · CVSS 4.0 · SSVC ({db.model})</SectionLabel>
      <Tiles>
        <StatTile label="Scored" value={f.comp_scored} color="#3fb950" />
        <StatTile label="Queued for next pass" value={f.comp_queued}
                  color={stalled ? "#f85149" : undefined} />
        <StatTile label={`Blocked (${db.retry_days}d retry)`} value={f.comp_blocked}
                  color={f.comp_blocked > 0 ? "#d29922" : undefined} />
        <StatTile label="Last scored" value={fmtTs(db.last_components_at)}
                  color={stalled ? "#f85149" : "var(--text-primary)"} />
      </Tiles>
      <SectionLabel>CWE</SectionLabel>
      <Tiles>
        <StatTile label="Scored" value={f.cwe_scored} color="#3fb950" />
        <StatTile label="Queued for next pass" value={f.cwe_queued} />
        <StatTile label={`Blocked (${db.retry_days}d retry)`} value={f.cwe_blocked}
                  color={f.cwe_blocked > 0 ? "#d29922" : undefined} />
        <StatTile label="Composite CWE" value={f.composite} total={f.cwe_scored}
                  title="Majority vote across CWE voters (vinnie-composite.timer, every 15 min)" />
        <StatTile label="Last scored" value={fmtTs(db.last_cwe_at)} />
      </Tiles>

      <HourlyChart title="Scores written per hour (last 24h)" rows={db.hourly} now={now} bars={[
        { key: "components", name: "CVSS/SSVC", color: "#3fb950" },
        { key: "cwe", name: "CWE", color: "#58a6ff" },
        { key: "composite", name: "Composite CWE", color: "#a371f7" },
        { key: "failed", name: "Failed attempts", color: "#f85149" },
      ]} />

      {comp.state && (
        <RecentTable title="Recent CVSS/SSVC scoring" rows={comp.state.recent}
                     empty="Nothing scored since the service started."
                     columns={[TIME_COL, CVE_COL, STATUS_COL, { label: "Result", key: "scores" }]} />
      )}
      {cwe.state && (
        <RecentTable title="Recent CWE scoring" rows={cwe.state.recent}
                     empty="Nothing scored since the service started."
                     columns={[TIME_COL, CVE_COL, STATUS_COL, { label: "Result", key: "scores" }]} />
      )}
    </>
  );
}

function CpeTab({ data, now }) {
  const svc = data.service;
  const db = data.db;
  return (
    <>
      <div className="panel" style={{ marginBottom: 12, padding: "12px 16px" }}>
        <ServiceHeader name="vinnie-cpe-selector" what="LLM picks CPEs for CVEs whose record has none"
                       svc={svc} error={data.service_error} now={now}
                       right={svc && `${svc.provider} · ${svc.model} · ${svc.workers} workers`}
                       progress={svc && `${svc.extracted} with CPEs, ${svc.skipped} none, ${svc.errors} errors`} />
      </div>
      <Tiles>
        <StatTile label={`CVEs in window (${db.window_days}d)`} value={db.in_window} />
        <StatTile label="CPEs from the CVE record" value={db.canonical}
                  title="cpes_json from the CNA/NVD record; no LLM needed" />
        <StatTile label="CPEs from the LLM" value={db.llm} color="#3fb950" />
        <StatTile label="LLM found none" value={db.llm_empty}
                  title="The LLM answered with no CPEs; not asked again" />
        <StatTile label="Not answered yet" value={db.never}
                  title="Queued for the next pass: never asked, the LLM call failed, or the record has no description" />
        {svc && svc.total > 0 && (
          <StatTile label={svc.status === "running" ? "Done (this pass)" : "Done (last pass)"}
                    value={svc.done} total={svc.total} />
        )}
      </Tiles>
      {svc && (
        <RecentTable title="Recent CVEs" rows={svc.recent} empty="Nothing processed since the service started."
                     columns={[TIME_COL, CVE_COL, STATUS_COL, { label: "CPEs", key: "count", align: "right" }]} />
      )}
    </>
  );
}

function PrevalenceTab({ data, now }) {
  const svc = data.service;
  const db = data.db;
  const lookups = svc ? svc.products_from_cache + svc.products_from_llm : 0;
  return (
    <>
      <div className="panel" style={{ marginBottom: 12, padding: "12px 16px" }}>
        <ServiceHeader name="vinnie-prevalence" what="how widely each affected product is deployed"
                       svc={svc} error={data.service_error} now={now}
                       right={svc && `${svc.provider} · ${svc.model || "default model"} · ${svc.workers} workers`}
                       progress={svc && `${svc.snapshotted} snapshots`} />
        {db.incomplete > 0 && (
          <Banner>
            {db.incomplete} snapshots in the window include products the LLM could not assess (for example
            while OpenRouter was out of credits). Snapshots are never recomputed, so these stay incomplete
            even after the product is assessed in the master list.
          </Banner>
        )}
      </div>
      <Tiles>
        <StatTile label={`Waiting for a snapshot (${db.window_days}d)`} value={db.queued} />
        <StatTile label="Snapshots" value={db.snapshots} color="#3fb950" />
        <StatTile label="Incomplete snapshots" value={db.incomplete}
                  color={db.incomplete > 0 ? "#d29922" : undefined} />
        <StatTile label="Snapshots with no products" value={db.no_products}
                  title="The CVE record lists no affected vendor/product" />
        <StatTile label="Products assessed" value={db.products_assessed} total={db.products} />
        {svc && lookups > 0 && (
          <StatTile label="Lookups answered from master list (last pass)"
                    value={`${Math.round((svc.products_from_cache / lookups) * 100)}%`}
                    title={`${svc.products_from_cache} from the master list, ${svc.products_from_llm} needed the LLM, ${svc.products_added} new products`} />
        )}
      </Tiles>
      <HourlyChart title="Snapshots per hour (last 24h)" rows={db.hourly} now={now}
                   bars={[{ key: "count", name: "Snapshots", color: "#58a6ff" }]} />
      {svc && (
        <RecentTable title="Recent CVEs" rows={svc.recent} empty="Nothing snapshotted since the service started."
                     columns={[TIME_COL, CVE_COL, STATUS_COL,
                               { label: "Products", key: "products", align: "right" },
                               { label: "From list", key: "cache", align: "right" },
                               { label: "From LLM", key: "llm", align: "right" },
                               { label: "Overall", key: "global" }]} />
      )}
    </>
  );
}

// Queue overview rows, in pipeline order; `tab` is the stage tab a row opens.
const QUEUE_ROWS = [
  { key: "sync", tab: "sync", label: "Repo Sync", unit: "" },
  { key: "import", tab: "import", label: "CVE Import", unit: "" },
  { key: "refs", tab: "refs", label: "Reference Collection", unit: "CVEs with unfetched refs" },
  { key: "tagging", tab: "tagging", label: "Reference Tagging", unit: "CVEs with untagged refs" },
  { key: "components", tab: "scoring", label: "Scoring: CVSS + SSVC", unit: "CVEs ready to score" },
  { key: "cwe", tab: "scoring", label: "Scoring: CWE", unit: "CVEs ready to score" },
  { key: "cpe", tab: "cpe", label: "CPE Selection", unit: "CVEs the LLM hasn't answered" },
  { key: "prevalence", tab: "prevalence", label: "Prevalence", unit: "CVEs without a snapshot" },
];

function fmtCount(n) {
  return n == null ? "—" : n.toLocaleString();
}

function heldBack(key, s, retryDays) {
  if (key === "tagging") {
    const parts = [];
    if (s.held) parts.push(`${fmtCount(s.held)} held (unparseable reply)`);
    if (s.backoff) parts.push(`${fmtCount(s.backoff)} in retry backoff`);
    return parts.join(", ");
  }
  if (s.blocked) return `${fmtCount(s.blocked)} blocked by a failed attempt (${retryDays}d)`;
  return "";
}

function QueueTab({ data, now, onOpen }) {
  const th = { padding: "8px 12px", fontSize: 12, color: "var(--text-secondary)", textAlign: "left",
               borderBottom: "1px solid var(--border)", whiteSpace: "nowrap" };
  const td = { padding: "8px 12px", fontSize: 13, borderBottom: "1px solid var(--border)" };
  const muted = { color: "var(--text-muted)" };
  const total = QUEUE_ROWS.reduce((n, r) => n + (data.stages[r.key].queued || 0), 0);
  return (
    <>
      <Tiles>
        <StatTile label="Items queued across stages" value={fmtCount(total)}
                  title="Sum of the Queued column; a CVE can be queued in more than one stage" />
        <StatTile label="Services down"
                  value={QUEUE_ROWS.filter((r) => data.stages[r.key].service.status === "down").length}
                  color={QUEUE_ROWS.some((r) => data.stages[r.key].service.status === "down") ? "#f85149" : undefined} />
      </Tiles>
      <div className="panel" style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={th}>Stage</th>
              <th style={th}>Service</th>
              <th style={{ ...th, textAlign: "right" }}>Queued</th>
              <th style={th}>Held back</th>
              <th style={{ ...th, textAlign: "right" }} title="CVEs whose refs aren't all fetched and tagged yet">
                Waiting on refs
              </th>
              <th style={th}>Last pass</th>
              <th style={th}>Next pass</th>
            </tr>
          </thead>
          <tbody>
            {QUEUE_ROWS.map((r) => {
              const s = data.stages[r.key];
              const svc = s.service;
              const running = svc.status === "running";
              const nextIn = !running && svc.next_run ? svc.next_run - now / 1000 : null;
              const held = heldBack(r.key, s, data.retry_days);
              return (
                <tr key={r.key} onClick={() => onOpen(r.tab)} style={{ cursor: "pointer" }}
                    title={`Open the ${TAB_LABELS[r.tab]} tab`}>
                  <td style={td}><strong>{r.label}</strong></td>
                  <td style={td}>
                    <StatusBadge status={svc.status || "down"} />
                    {running && svc.total > 0 && (
                      <span style={{ fontSize: 12, color: "var(--text-secondary)", marginLeft: 8 }}>
                        {svc.done} / {svc.total}
                      </span>
                    )}
                  </td>
                  <td style={{ ...td, textAlign: "right" }}>
                    {s.queued == null ? <span style={muted} title="Pulls from GitHub; no database queue">—</span> : (
                      <>
                        <span style={{ fontWeight: 700, color: s.queued > 0 ? "var(--text-primary)" : "#3fb950" }}>
                          {fmtCount(s.queued)}
                        </span>
                        <div style={{ fontSize: 11, ...muted }}>
                          {r.unit}{r.key === "tagging" && s.queued_refs ? ` (${fmtCount(s.queued_refs)} refs)` : ""}
                        </div>
                      </>
                    )}
                  </td>
                  <td style={{ ...td, fontSize: 12, color: held ? "#d29922" : "var(--text-muted)" }}>
                    {held || "—"}
                  </td>
                  <td style={{ ...td, textAlign: "right" }}>
                    {s.upstream != null ? fmtCount(s.upstream) : <span style={muted}>—</span>}
                  </td>
                  <td style={{ ...td, fontSize: 12, color: "var(--text-secondary)", whiteSpace: "nowrap" }}>
                    {svc.last_run ? fmtAge(svc.last_run, now) : "—"}
                  </td>
                  <td style={{ ...td, fontSize: 12, color: "var(--text-secondary)", whiteSpace: "nowrap" }}>
                    {running ? "running now" : nextIn != null && nextIn > 0 ? `in ${fmtDuration(nextIn)}` : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function _tabFromPath() {
  const m = window.location.pathname.match(/^\/pipeline\/(\w+)/);
  return m && TABS.includes(m[1]) ? m[1] : TABS[0];
}

export default function PipelinePanel() {
  const [tab, setTabState] = useState(_tabFromPath);
  const [tabData, setTabData] = useState({});
  const [error, setError] = useState(null);
  const [now, setNow] = useState(Date.now());

  // Keep the URL in sync with the active tab (/pipeline, /pipeline/<tab>).
  const setTab = (t) => {
    const path = t === TABS[0] ? "/pipeline" : `/pipeline/${t}`;
    if (window.location.pathname !== path) window.history.pushState(null, "", path);
    setTabState(t);
  };
  useEffect(() => {
    const onPop = () => {
      if (window.location.pathname.startsWith("/pipeline")) setTabState(_tabFromPath());
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const load = useCallback((t) => {
    TAB_FETCHERS[t]()
      .then((d) => { setTabData((prev) => ({ ...prev, [t]: d })); setError(null); setNow(Date.now()); })
      .catch((e) => setError(e.message));
  }, []);

  // Only the visible tab polls.
  useEffect(() => {
    setError(null);
    load(tab);
    const id = setInterval(() => load(tab), REFRESH_MS);
    return () => clearInterval(id);
  }, [tab, load]);

  const descriptions = {
    queue: "What each stage still has to do, over the last 14 days of new or updated CVEs. Each stage only picks up a CVE once the stage before it has finished with it. Click a row for that stage's tab. Refreshes every 10s.",
    sync: "vinnie-repo-sync pulls the cvelistV5 and CISA vulnrichment GitHub repos every 15 minutes; CVE import reads these local clones. Refreshes every 10s.",
    scoring: "Once all of a CVE's refs are fetched and tagged, vinnie-llm-component-scoring makes one LLM call that is mapped to CVSS 3.1, CVSS 4.0 and SSVC, and vinnie-llm-scoring picks the CWE. Both run hourly. Refreshes every 10s; the funnel counts every 5 minutes.",
    cpe: "vinnie-cpe-selector asks the LLM for CPEs, grounded in the NVD CPE dictionary, for CVEs whose record has none. Runs every 15 minutes. Refreshes every 10s.",
    prevalence: "vinnie-prevalence saves a snapshot of how widely each affected product is deployed, reusing the products master list and calling the LLM only for new products. Runs hourly. Refreshes every 10s.",
    import: "vinnie-cve-import pulls new and updated CVEs from the cvelistV5 deltaLog every 15 minutes. Refreshes every 10s.",
    refs: "vinnie-ref-collector fetches each CVE's reference pages into the refdata cache every 15 minutes. Scoring starts only once all of a CVE's refs are processed. Refreshes every 10s.",
    tagging: "vinnie-ref-tagging labels each reference (fix, advisory, exploit, …) with the LLM, 5 refs per call, every 15 minutes; known domains are tagged by rule. Scoring starts only once all of a CVE's refs are tagged. Refreshes every 10s.",
  };
  const data = tabData[tab];

  return (
    <div>
      <div className="panel" style={{ marginBottom: 12 }}>
        <div className="panel-header" style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ display: "flex", gap: 2 }}>
            {TABS.map((t) => (
              <button key={t} className={`table-tab ${tab === t ? "active" : ""}`}
                      onClick={() => setTab(t)}>
                {TAB_LABELS[t]}
              </button>
            ))}
          </div>
        </div>
        <p style={{ fontSize: 12, color: "var(--text-secondary)", margin: "6px 0 0" }}>
          {descriptions[tab]}
          {data && data.db_as_of && now - new Date(data.db_as_of).getTime() > 15000 && (
            <> Database counts as of {fmtTime(data.db_as_of)}.</>
          )}
        </p>
      </div>

      {error && <div className="error">API error: {error}</div>}
      {!data && !error && <div className="loading">Loading...</div>}
      {tab === "queue" && data && <QueueTab data={data} now={now} onOpen={setTab} />}
      {tab === "sync" && data && <SyncTab data={data} now={now} />}
      {tab === "import" && data && <ImportTab data={data} now={now} />}
      {tab === "refs" && data && <RefsTab data={data} now={now} />}
      {tab === "tagging" && data && <TaggingTab data={data} now={now} />}
      {tab === "scoring" && data && <ScoringTab data={data} now={now} />}
      {tab === "cpe" && data && <CpeTab data={data} now={now} />}
      {tab === "prevalence" && data && <PrevalenceTab data={data} now={now} />}
    </div>
  );
}
